import { useEffect, useState } from "react";
import { Modal, Skeleton, Grid } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import {
	FormRequestFlightAdditionalInfo,
	FormRequestFlightInfo,
	FormRequestFlightSelectCategory,
	FormRequestFlightSummary,
} from "@/components/organisms";
import { DATETIME_FORMAT_STANDARD, FLIGHT_REQUEST_OPTIONS, NOTIFICATION_TYPE } from "@/config/constants";
import { initFormRequest } from "@/utils/initializing";
import { createCharterBooking } from "@/stores/apis/apiCharter";
import { readTrafficSource } from "@/utils/trafficSource";
import { toast } from "@/modules/toast";
import { ModalStatus } from "../ModalStatus";
import styled from "styled-components";
import dayjs from "dayjs";
import { noop } from "lodash";
const { useBreakpoint } = Grid;

const FlightRequestStep = {
	SELECT_LEG_TYPE: 0,
	SELECT_AIRCRAFT_CATEGORY: 1,
	SELECT_ADDITIONAL_INFO: 2,
	SUMMARY: 3,
};

export function ModalRequestQuote({
	visible = false,
	onClose,
	forceSubmitRequest = false,
	onUpdateForceSubmitRequest = noop,
}) {
	const screens = useBreakpoint();
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(FlightRequestStep.SELECT_LEG_TYPE);
	const [formRequest, setFormRequest] = useState(initFormRequest);
	const [showStatus, setShowStatus] = useState(false);

	const onNext = () => {
		setStep(step + 1);
	};

	const onBack = () => {
		setStep(step - 1);
	};

	const onSubmitRequest = async () => {
		let legsValue = [...(formRequest?.legs || [])];
		if (formRequest.type === FLIGHT_REQUEST_OPTIONS.ONE_WAY) {
			let flight = formRequest?.legs[0];
			legsValue = [flight];
		} else if (formRequest.type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP) {
			let flight1 = formRequest?.legs[0];
			let flight2 = formRequest?.legs[1];
			legsValue = [flight1, flight2];
		} else if (formRequest.type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG) {
			legsValue = [...(formRequest?.legs || [])];
		}

		let payload = {
			type: formRequest.type,
			legs:
				legsValue?.map((v) => {
					// format time range with timezone
					let timezone = v?.from_airport?.location?.timezone;
					let departure_datetime = v?.departure_datetime;
					if (departure_datetime && timezone) {
						let value = dayjs(departure_datetime).format(DATETIME_FORMAT_STANDARD);
						departure_datetime = dayjs.tz(value, timezone).toISOString();
					}

					let departure_window_datetime = v?.departure_window_datetime;
					if (departure_window_datetime && timezone) {
						let value = dayjs(departure_window_datetime).format(DATETIME_FORMAT_STANDARD);
						departure_window_datetime = dayjs.tz(value, timezone).toISOString();
					}

					return {
						from_airport: v?.from_airport?._id,
						to_airport: v?.to_airport?._id,
						departure_datetime: departure_datetime,
						departure_window_datetime: departure_window_datetime,
						passengers: v?.passengers ?? 1,
					};
				}) ?? [],
			aircraft_categories: formRequest.aircraft_categories?.map((v) => v._id) ?? [],
			note: formRequest.note,
			additional_info: {
				is_bring_pet: formRequest.additional_info.is_bring_pet,
				is_bring_oversize_luggage: formRequest.additional_info.is_bring_oversize_luggage,
				is_interest_empty_leg: formRequest.additional_info.is_interest_empty_leg,
			},
		};

		if (loading) return;
		setLoading(true);

		const traffic_source = readTrafficSource();

		let result = await createCharterBooking({
			...payload,
			traffic_source,
		});
		if (result && result.success) {
			setShowStatus(true);
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: result?.errors?.message ?? "Flight Request Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	useEffect(() => {
		if (
			forceSubmitRequest &&
			step === FlightRequestStep.SUMMARY &&
			formRequest?.aircraft_categories?.length > 0 &&
			formRequest?.legs?.[0]?.from_airport
		) {
			onSubmitRequest();
			onUpdateForceSubmitRequest(false);
		}
	}, [forceSubmitRequest]);

	const _renderContentByType = () => {
		switch (step) {
			case FlightRequestStep.SELECT_LEG_TYPE:
				return (
					<FormRequestFlightInfo
						data={formRequest}
						onChange={(values) => setFormRequest({ ...values })}
						onNext={onNext}
					/>
				);
			case FlightRequestStep.SELECT_AIRCRAFT_CATEGORY:
				return (
					<FormRequestFlightSelectCategory
						data={formRequest}
						onChange={(values) => setFormRequest({ ...values })}
						onNext={onNext}
						onBack={onBack}
					/>
				);
			case FlightRequestStep.SELECT_ADDITIONAL_INFO:
				return (
					<FormRequestFlightAdditionalInfo
						data={formRequest}
						onChange={(values) => setFormRequest({ ...values })}
						onNext={onNext}
						onBack={onBack}
					/>
				);
			case FlightRequestStep.SUMMARY:
				return (
					<FormRequestFlightSummary
						data={formRequest}
						onNext={onNext}
						onBack={onBack}
						onSubmitRequest={onSubmitRequest}
						loading={loading}
						formatTimezone={false}
					/>
				);
			default:
				return <Skeleton style={{ margin: "0 12px" }} />;
		}
	};

	const getWidthModal = () => {
		let width = "auto";
		if (screens.md || screens.lg || screens.xl || screens.xxl) {
			width = "1000px";
		}
		return width;
	};

	return (
		<Modal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => {
				setStep(FlightRequestStep.SELECT_LEG_TYPE);
				setFormRequest(initFormRequest);
				onClose(false);
			}}
			footer={null}
			width={getWidthModal()}
			wrapClassName="auth-wrapper"
			style={{ padding: 0 }}
			maskClosable={false}
			destroyOnClose
			data-testid="modal_flightRequest"
		>
			<Container>
				{_renderContentByType()}
				<ModalStatus
					visible={showStatus}
					status={NOTIFICATION_TYPE.SUCCESS}
					title="Flight Request Sent Successfully!"
					note="Thank you for your request! Our team will be in contact with you shortly."
					onClose={() => {
						setShowStatus(false);
						setTimeout(() => {
							setStep(FlightRequestStep.SELECT_LEG_TYPE);
							setFormRequest(initFormRequest);
							onClose();
						}, 300);
					}}
				/>
			</Container>
		</Modal>
	);
}

const Container = styled.div`
	max-width: 1000px;
	min-width: 343px;
	padding: 60px;

	@media (max-width: 991px) {
		padding: 40px;
	}

	@media (max-width: 767px) {
		padding: 32px;
	}

	@media (max-width: 575px) {
		padding: 24px;
	}
`;
